// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useEffect, useState, useCallback } from 'react'
import { Truck, Snowflake, PenLine, RefreshCw, MapPin } from 'lucide-react'
import * as api from '../../api/client'
import { useAuth } from '../../auth/AuthContext'
import { carrierLabel } from '../../lib/carriers'

/**
 * Shipping section for the Rx detail page (Sprint 18).
 *
 * Two modes:
 *  - Rx is dispensed, no active shipment → "Ship this Rx" action (drawer with
 *    carrier, service level, special-handling flags, optional quote-confirm).
 *  - Rx already shipped → status card with tracking, event timeline, and POD.
 *
 * Security/compliance guardrails (per team review):
 *  - Rendered only when the caller holds shipping.read; the Ship action only
 *    when they hold shipping.create.
 *  - The patient address is shown only inside the ship drawer (where staff
 *    must confirm it), never in the at-a-glance card.
 *  - Manual "Refresh tracking" is throttled to avoid hammering the carrier API
 *    (the background poll job is the primary status mechanism).
 *  - POD signature image is rendered from the authenticated URL only; never
 *    persisted to browser storage.
 */
export default function RxShippingSection({ rxId, rxState }: { rxId: string; rxState: string }) {
  const { hasPermission } = useAuth()
  const canRead = hasPermission('shipping.read')
  const canShip = hasPermission('shipping.create')

  const [shipment, setShipment] = useState<api.ShipmentDetail | null>(null)
  const [events, setEvents] = useState<api.ShipmentEvent[]>([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [lastRefresh, setLastRefresh] = useState(0)
  const [drawerOpen, setDrawerOpen] = useState(false)

  const load = useCallback(async (refresh = false) => {
    try {
      const res = await api.getRxShipment(rxId, refresh)
      setShipment(res.shipment)
      setEvents(res.events ?? [])
    } catch {
      /* leave as-is; the section just won't show a shipment */
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [rxId])

  useEffect(() => { if (canRead) load() }, [canRead, load])

  // Only show the section for shippable / shipped states.
  const relevant = ['dispensed', 'out_for_delivery', 'delivered'].includes(rxState)
  if (!canRead || !relevant) return null

  // throttle manual refresh to once per 10s
  const onRefresh = () => {
    const now = Date.now()
    if (now - lastRefresh < 10_000 || refreshing) return
    setRefreshing(true)
    setLastRefresh(now)
    load(true)
  }

  return (
    <section style={cardStyle}>
      <header style={sectionHeader}>
        <Truck size={14} aria-hidden="true" />
        <h3 style={sectionH3}>Shipping</h3>
        {shipment && (
          <button
            type="button"
            onClick={onRefresh}
            disabled={refreshing}
            style={refreshBtn}
            title="Refresh tracking"
          >
            <RefreshCw size={12} style={{ animation: refreshing ? 'spin 1s linear infinite' : undefined }} />
            {refreshing ? ' Refreshing…' : ' Refresh'}
          </button>
        )}
      </header>
      <div style={{ padding: '12px 16px 16px' }}>
        {loading ? (
          <div style={mutedText}>Loading…</div>
        ) : shipment ? (
          <ShipmentCard shipment={shipment} events={events} />
        ) : canShip && rxState === 'dispensed' ? (
          <div>
            <div style={{ ...mutedText, marginBottom: 10 }}>Not yet shipped.</div>
            <button type="button" onClick={() => setDrawerOpen(true)} style={primaryBtn}>
              <Truck size={13} /> Ship this Rx
            </button>
          </div>
        ) : (
          <div style={mutedText}>No shipment on this prescription.</div>
        )}
      </div>

      {drawerOpen && (
        <ShipDrawer rxId={rxId} onClose={() => setDrawerOpen(false)} onShipped={() => { setDrawerOpen(false); setLoading(true); load() }} />
      )}
    </section>
  )
}

// ── Status card (already shipped) ──────────────────────────────────────────────
function ShipmentCard({ shipment, events }: { shipment: api.ShipmentDetail; events: api.ShipmentEvent[] }) {
  return (
    <div>
      {/* Download POD (Patrick 2026-07-16): API-linked, role-aware ($ manager-only), no navigation. */}
      <div style={{ marginBottom: 10 }}>
        <button type="button"
                onClick={() => api.openShipmentPodDoc(shipment.id).catch(() => { alert('Could not open the delivery document. Please try again.') })}
                title="Download the Proof-of-Delivery / shipment status document"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 8,
                         border: '1px solid var(--color-border)', background: 'var(--color-surface)',
                         color: 'var(--color-text)', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}>
          ⬇ Download POD
        </button>
      </div>
      <div style={fieldRow}><div style={fieldLabel}>Carrier</div><div style={fieldValue}>{carrierLabel(shipment.carrier)}</div></div>
      <div style={fieldRow}><div style={fieldLabel}>Status</div><div style={fieldValue}><StatusPill status={shipment.status} /></div></div>
      <div style={fieldRow}><div style={fieldLabel}>Tracking #</div><div style={fieldValue}>{shipment.tracking_number || '—'}</div></div>
      {(shipment.cold_chain || shipment.require_signature || shipment.adult_signature || shipment.saturday_delivery) && (
        <div style={fieldRow}>
          <div style={fieldLabel}>Handling</div>
          <div style={{ ...fieldValue, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {shipment.cold_chain && <Badge icon={Snowflake} label="Cold chain" tone="#0369a1" />}
            {shipment.require_signature && <Badge icon={PenLine} label="Signature" tone="#7c3aed" />}
            {shipment.adult_signature && <Badge icon={PenLine} label="Adult sig" tone="#7c3aed" />}
            {shipment.saturday_delivery && <Badge icon={Truck} label="Saturday" tone="#a16207" />}
          </div>
        </div>
      )}
      {shipment.special_instructions && (
        <div style={fieldRow}><div style={fieldLabel}>Instructions</div><div style={fieldValue}>{shipment.special_instructions}</div></div>
      )}

      {/* POD on delivery */}
      {shipment.status === 'delivered' && (
        <div style={podBox}>
          <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 4 }}>Proof of delivery</div>
          {shipment.pod_signature_name && (
            <div style={{ fontSize: 12 }}>Signed by: {shipment.pod_signature_name}</div>
          )}
          {shipment.delivered_at && (
            <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
              {new Date(shipment.delivered_at).toLocaleString()}
            </div>
          )}
          {shipment.pod_image_url && (
            // POD signature image — rendered from authenticated URL, never stored.
            <img src={shipment.pod_image_url} alt="Delivery signature" style={{ maxWidth: 200, marginTop: 6, border: '1px solid var(--color-border)', borderRadius: 4 }} />
          )}
        </div>
      )}

      {/* event timeline */}
      {events.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 6 }}>Tracking history</div>
          <ol style={timeline}>
            {events.map((e, i) => (
              <li key={i} style={timelineItem}>
                <MapPin size={11} style={{ color: 'var(--color-text-muted)', flexShrink: 0, marginTop: 2 }} />
                <div>
                  <div style={{ fontSize: 12 }}>{e.description || e.carrier_status_text || statusLabel(e.status)}</div>
                  <div style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
                    {e.location ? `${e.location} · ` : ''}{new Date(e.occurred_at).toLocaleString()}
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  )
}

// ── Ship drawer ────────────────────────────────────────────────────────────────
function ShipDrawer({ rxId, onClose, onShipped }: { rxId: string; onClose: () => void; onShipped: () => void }) {
  const [carriers, setCarriers] = useState<api.ShippingCarrier[]>([])
  const [carrierId, setCarrierId] = useState('')
  const [requireSig, setRequireSig] = useState(false)
  const [coldChain, setColdChain] = useState(false)
  const [adultSig, setAdultSig] = useState(false)
  const [saturday, setSaturday] = useState(false)
  const [instructions, setInstructions] = useState('')
  const [quote, setQuote] = useState<{ amount: number | null; detail: string } | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    api.listShippingCarriers().then(res => {
      const active = res.items.filter(c => c.is_active)
      setCarriers(active)
      if (active.length === 1) {
        setCarrierId(active[0].id)
        setColdChain(active[0].default_cold_chain)
        setRequireSig(active[0].default_signature)
      }
    }).catch(() => setError('Could not load carriers.'))
  }, [])

  const selected = carriers.find(c => c.id === carrierId)

  const getQuote = async () => {
    if (!carrierId) return
    setBusy(true); setError(null)
    try {
      const q = await api.quoteShipment({
        carrier_account_id: carrierId,
        require_signature: requireSig, adult_signature: adultSig,
        cold_chain: coldChain, saturday_delivery: saturday,
      })
      setQuote({ amount: q.amount, detail: q.detail })
    } catch (e: any) {
      setError(e?.payload?.detail || e?.message || 'Quote failed.')
    } finally { setBusy(false) }
  }

  const doShip = async () => {
    if (!carrierId) return
    setBusy(true); setError(null)
    try {
      await api.shipRx(rxId, {
        carrier_account_id: carrierId,
        require_signature: requireSig, adult_signature: adultSig,
        cold_chain: coldChain, saturday_delivery: saturday,
        special_instructions: instructions || undefined,
      })
      onShipped()
    } catch (e: any) {
      setError(e?.payload?.message || e?.payload?.detail || e?.message || 'Ship failed.')
    } finally { setBusy(false) }
  }

  return (
    <div style={overlay} onClick={onClose}>
      <div style={drawer} onClick={ev => ev.stopPropagation()}>
        <h3 style={{ margin: '0 0 12px', fontSize: 15 }}>Ship prescription</h3>

        <label style={labelStyle}>Carrier</label>
        <select value={carrierId} onChange={e => {
          setCarrierId(e.target.value); setQuote(null)
          const c = carriers.find(x => x.id === e.target.value)
          if (c) { setColdChain(c.default_cold_chain); setRequireSig(c.default_signature) }
        }} style={inputStyle}>
          <option value="">Select a carrier…</option>
          {carriers.map(c => (
            <option key={c.id} value={c.id}>
              {c.display_name}{c.is_test_mode ? ' (test)' : ''}
            </option>
          ))}
        </select>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, margin: '12px 0' }}>
          <Toggle checked={coldChain} onChange={setColdChain} icon={Snowflake} label="Cold chain (temperature-controlled)" />
          <Toggle checked={requireSig} onChange={setRequireSig} icon={PenLine} label="Signature required" />
          <Toggle checked={adultSig} onChange={setAdultSig} icon={PenLine} label="Adult signature required" />
          <Toggle checked={saturday} onChange={setSaturday} icon={Truck} label="Saturday delivery" />
        </div>

        <label style={labelStyle}>Special instructions</label>
        <textarea value={instructions} onChange={e => setInstructions(e.target.value)}
          placeholder="e.g. Leave with patient only" rows={2} style={{ ...inputStyle, resize: 'vertical' }} />

        {quote && (
          <div style={quoteBox}>
            Rate: <strong>{quote.amount != null ? `$${quote.amount.toFixed(2)}` : 'n/a'}</strong>
            <span style={{ color: 'var(--color-text-muted)', marginLeft: 6 }}>{quote.detail}</span>
          </div>
        )}
        {error && <div style={errorBox}>{error}</div>}

        <div style={{ display: 'flex', gap: 8, marginTop: 14, justifyContent: 'flex-end' }}>
          <button type="button" onClick={onClose} style={secondaryBtn} disabled={busy}>Cancel</button>
          {selected?.require_quote_confirm !== false && !quote ? (
            <button type="button" onClick={getQuote} style={primaryBtn} disabled={!carrierId || busy}>
              {busy ? 'Getting rate…' : 'Get rate'}
            </button>
          ) : (
            <button type="button" onClick={doShip} style={primaryBtn} disabled={!carrierId || busy}>
              {busy ? 'Shipping…' : quote ? 'Confirm & ship' : 'Ship'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

// ── small UI bits ───────────────────────────────────────────────────────────────
function StatusPill({ status }: { status: string }) {
  const tone = STATUS_TONE[status] ?? '#6b7280'
  return <span style={{ ...pill, background: `${tone}1a`, color: tone, borderColor: `${tone}55` }}>{statusLabel(status)}</span>
}
function Badge({ icon: Icon, label, tone }: { icon: any; label: string; tone: string }) {
  return <span style={{ ...badge, color: tone, borderColor: `${tone}55` }}><Icon size={10} /> {label}</span>
}
function Toggle({ checked, onChange, icon: Icon, label }: { checked: boolean; onChange: (v: boolean) => void; icon: any; label: string }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} />
      <Icon size={13} /> {label}
    </label>
  )
}

const STATUS_TONE: Record<string, string> = {
  created: '#6b7280', label_purchased: '#0369a1', in_transit: '#0369a1',
  out_for_delivery: '#a16207', delivered: '#15803d', exception: '#dc2626', cancelled: '#6b7280',
}
function statusLabel(s: string) {
  return ({ created: 'Created', label_purchased: 'Label purchased', in_transit: 'In transit',
    out_for_delivery: 'Out for delivery', delivered: 'Delivered', exception: 'Exception', cancelled: 'Cancelled' } as Record<string, string>)[s] ?? s
}

// ── styles (match RxDetailPage conventions) ──────────────────────────────────────
const cardStyle: React.CSSProperties = { background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 8, marginBottom: 12 }
const sectionHeader: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, padding: '10px 16px', borderBottom: '1px solid var(--color-border)' }
const sectionH3: React.CSSProperties = { margin: 0, fontSize: 13, fontWeight: 600, flex: 1 }
const fieldRow: React.CSSProperties = { display: 'flex', padding: '4px 0', fontSize: 13 }
const fieldLabel: React.CSSProperties = { width: 110, color: 'var(--color-text-muted)', flexShrink: 0 }
const fieldValue: React.CSSProperties = { flex: 1 }
const mutedText: React.CSSProperties = { color: 'var(--color-text-muted)', fontSize: 13 }
const primaryBtn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, background: 'var(--color-primary, #0369a1)', color: '#fff', border: 'none', borderRadius: 6, padding: '7px 12px', fontSize: 13, cursor: 'pointer' }
const secondaryBtn: React.CSSProperties = { background: 'transparent', color: 'var(--color-text)', border: '1px solid var(--color-border)', borderRadius: 6, padding: '7px 12px', fontSize: 13, cursor: 'pointer' }
const refreshBtn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 3, background: 'transparent', border: '1px solid var(--color-border)', borderRadius: 5, padding: '3px 8px', fontSize: 11, cursor: 'pointer', color: 'var(--color-text-muted)' }
const pill: React.CSSProperties = { display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, border: '1px solid' }
const badge: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 3, padding: '1px 6px', borderRadius: 8, fontSize: 10, fontWeight: 600, border: '1px solid' }
const podBox: React.CSSProperties = { marginTop: 12, padding: 10, background: '#15803d0d', border: '1px solid #15803d33', borderRadius: 6 }
const timeline: React.CSSProperties = { listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }
const timelineItem: React.CSSProperties = { display: 'flex', gap: 6, alignItems: 'flex-start' }
const overlay: React.CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', justifyContent: 'flex-end', zIndex: 1000 }
const drawer: React.CSSProperties = { width: 'min(440px, 100%)', height: '100%', background: 'var(--color-surface)', padding: 20, overflowY: 'auto', boxShadow: '-4px 0 16px rgba(0,0,0,0.15)' }
const labelStyle: React.CSSProperties = { display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4, marginTop: 8 }
const inputStyle: React.CSSProperties = { width: '100%', padding: '7px 10px', fontSize: 13, border: '1px solid var(--color-border)', borderRadius: 6, boxSizing: 'border-box' }
const quoteBox: React.CSSProperties = { marginTop: 12, padding: 10, background: 'var(--color-bg, #f8fafc)', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 13 }
const errorBox: React.CSSProperties = { marginTop: 10, padding: 8, background: '#dc26260d', border: '1px solid #dc262633', borderRadius: 6, fontSize: 12, color: '#dc2626' }
